import type { InjectionKey } from 'vue';
import { FILTER_SEARCH_OPERATORS } from '@/common/components/molecules/filterSearch/filterSearch.define';
import { GridRawRowDataType } from '@/common/components/molecules/modernGrid/modernGrid.types';

export type FilterSearchOperator = (typeof FILTER_SEARCH_OPERATORS)[number];

export type FilterSearchResultToken = {
  key: string;
  operator: string;
  value: string;
};

export type FilterSearchSpecificFilterFnKey =
  | 'toBytes'
  | 'toKiB'
  | 'toMB'
  | 'readableBytes'
  | 'toMs'
  | 'rendererTypeSec'
  | 'toSecByUnit'
  | 'toBps'
  | 'mWToW'
  | 'bytesToMiB'
  | 'toUnixTime'
  | 'age';

export type FilterSearchCustomFn = (cellValue: any, token: FilterSearchResultToken) => boolean;

export interface FilterItem {
  key: { id: string; name: string };
  operators: FilterSearchOperator[];
  values: {
    multi?: boolean;
    items?: { id: string; name: string }[];
  };
  customFilterFn?: FilterSearchCustomFn;
  columnType?: string; // 컬럼의 원본 타입 (stringNumber 등 고유값 추출 판단에 사용)
  defaultUnit?: string; // 단위를 포함한 숫자를 대상으로 검색할 때, 사용자가 unit을 입력하지 않고 숫자만 입력할 경우 자동으로 뒤에 붙여주는 unit
  defaultSearchField?: boolean; // 기본 검색 컬럼 여부
}

export type TokenStatus =
  | 'EMPTY'
  | 'KEY_ING'
  | 'KEY_DONE'
  | 'OPERATOR_ING'
  | 'OPERATOR_DONE'
  | 'VALUE_ING'
  | 'VALUE_DONE';

export interface FilterDropdownItem {
  id: string;
  name: string;
  disabled?: boolean;
  checked?: boolean;
  isDefaultColumn?: boolean;
}

export type FilterDropdownInfoCategories = 'key' | 'operator' | 'value';

export interface FilterDropdownInfo {
  category: FilterDropdownInfoCategories;
  items: FilterDropdownItem[];
  filteredItems: FilterDropdownItem[];
  multi: boolean;
  isEditing: boolean;
  isAutoValueItems: boolean;
}

export interface FilterSelectedValues {
  logicalOperator: string;
  tokens: {
    key: string;
    operator: string;
    value: string;
  }[];
}

export interface CustomFilterOption {
  id: string;
  filterFn: (row: any[] | GridRawRowDataType, token: FilterSearchResultToken) => boolean; // NOTE: EvGrid는 any[] 타입, ModernGrid 는 GridRawRowDataType 타입
}

export interface FilterToken {
  id: number;
  key: FilterDropdownItem;
  operator: FilterDropdownItem | undefined;
  values: FilterDropdownItem[];
  status: TokenStatus;
}

export interface FinishedFilterToken extends FilterToken {
  status: 'VALUE_DONE';
  operator: FilterDropdownItem;
}

export interface EditingFilterToken extends FilterToken {
  backupValues: FilterDropdownItem[];
}

export interface FilterHistory {
  token: FilterToken;
  createdTime: number;
}

export interface FilterSearchExposed {
  clearFilterSearchToken: () => void;
}

export interface FilterSearchRegistry {
  register: (instance: FilterSearchExposed) => void;
  unregister: (instance: FilterSearchExposed) => void;
}

export const FILTER_SEARCH_REGISTRY_KEY: InjectionKey<FilterSearchRegistry> =
  Symbol('filterSearchRegistry');

export interface ExternalTextSearchHandler {
  clear: () => void;
  triggerSearch?: () => void;
}

export const EXTERNAL_TEXT_SEARCH_KEY: InjectionKey<ExternalTextSearchHandler> =
  Symbol('externalTextSearch');

export interface ExternalFilterSearchHandler {
  clear: () => void;
}

export const EXTERNAL_FILTER_SEARCH_KEY: InjectionKey<ExternalFilterSearchHandler> =
  Symbol('externalFilterSearch');
